import React, { useMemo, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { LocalizedButton as Button, LocalizedChip as Chip } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import {
  AutoAwesome,
  Calculate,
  Favorite,
  Public,
  Science,
  Verified,
  Psychology,
  Description,
  Groups,
  Insights,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

type Lang = 'en' | 'hi';

const content = {
  en: {
    brand: 'Kundlistar',
    badge: 'Vedic • KP • BNN • Jaimini',
    title: 'About Kundlistar',
    hero:
      'Kundlistar is a modern astrology platform created to make deep Vedic astrology, KP astrology, BNN, Jaimini, numerology, Astro-Vastu, and advanced kundli analysis more accessible, structured, and easy to understand.',
    hero2:
      'Our goal is to help astrologers, astrology students, and spiritual seekers analyze birth charts faster, deeper, and with more confidence — without jumping between multiple tools, notes, PDFs, and manual calculations.',
    createChart: 'Create Chart',
    viewPlans: 'View Plans',
    focusTitle: 'Our Focus',
    focus: [
      ['Accuracy', 'Reliable chart calculations and structured astrology logic.'],
      ['Depth', 'Multiple Jyotish systems in one professional workspace.'],
      ['Clarity', 'Readable charts, reports, tables, and mobile-friendly views.'],
      ['Responsibility', 'Guidance for awareness, learning, and better decision-making.'],
    ],
    pillars: [
      {
        title: 'Advanced Astrology Tools',
        description:
          'Kundlistar brings Vedic, KP, BNN, Jaimini, Nadi, Panchang, transits, matching, numerology, and report workflows into one focused astrology platform.',
        icon: <Calculate />,
      },
      {
        title: 'Built For Real Practice',
        description:
          'The platform is designed for astrologers, learners, and serious seekers who need repeatable calculations, saved charts, clean analysis views, and professional reporting.',
        icon: <Science />,
      },
      {
        title: 'Guidance With Responsibility',
        description:
          'Astrological interpretations are shared as spiritual, educational, and self-reflection guidance. Users are encouraged to use personal judgment for important life decisions.',
        icon: <Verified />,
      },
    ],
    whyTitle: 'Why We Built Kundlistar',
    why1:
      'Astrology work often requires moving between many tools, saved charts, notes, PDFs, Panchang references, and manual calculations. Kundlistar brings these workflows together so users can spend less time switching screens and more time understanding the chart.',
    why2:
      'We are building Kundlistar as a complete astrology analysis ecosystem — not just a kundli generator. It is made for serious study, practical consultation, and deeper interpretation.',
    offersTitle: 'What Kundlistar Offers',
    capabilities: [
      'Vedic kundli generation',
      'KP astrology analysis',
      'Bhrigu Nandi Nadi workflows',
      'Jaimini astrology support',
      'Divisional charts',
      'Dasha and transit analysis',
      'Panchang and Navtara',
      'Horoscope matching',
      'Horary charts',
      'Personalised PDF reports',
      'Vedic numerology suggestions',
      'Astro-Vastu insights',
      'KP and BNN Vastu logic',
      'Panch Pakshi calculations',
      'Gemstone recommendations',
      'Web and mobile app access',
    ],
    missionTitle: 'Our Mission',
    mission:
      'Our mission is to make advanced astrology easier to access, learn, and apply. Kundlistar helps users explore planetary combinations, dasha periods, transits, chart strengths, yogas, matching, numerology, and Vastu insights through a clean and organized platform.',
    visionTitle: 'Our Vision',
    vision:
      'Our vision is to make Kundlistar one of the most trusted astrology SaaS platforms from India, serving both Indian and global astrology users through accurate calculations, practical reports, mobile access, and continuous feature improvements.',
    audienceTitle: 'Built For Astrologers, Learners, And Seekers',
    audienceText:
      'Kundlistar is useful for anyone who wants to go beyond surface-level astrology. Whether you are learning Jyotish, giving consultations, researching charts, or trying to understand your own life patterns, the platform gives you structured tools in one place.',
    audiences: [
      'Professional astrologers',
      'Astrology students',
      'Spiritual seekers',
      'Researchers of Jyotish',
      'Users looking for detailed kundli reports',
      'Consultants who need faster chart analysis',
    ],
    valuesTitle: 'Our Values',
    values: [
      {
        title: 'Accuracy',
        text: 'We focus on reliable chart calculations and structured astrological logic so users can work with confidence.',
      },
      {
        title: 'Depth',
        text: 'Kundlistar combines multiple astrology systems instead of limiting users to basic chart generation.',
      },
      {
        title: 'Clarity',
        text: 'Complex astrology concepts are presented through clean charts, readable tables, guided reports, and simple explanations.',
      },
      {
        title: 'Practical Use',
        text: 'Our tools are designed for real-life areas such as career, marriage, finance, health, education, property, and personal growth.',
      },
    ],
    ctaTitle: 'Start Your Astrology Journey',
    ctaText:
      'Create a birth chart, explore Vedic/KP/BNN/Jaimini layers, save your analysis, and generate meaningful insights in one place.',
    ctaButton: 'Calculate Kundli',
  },

  hi: {
    brand: 'कुंडलिस्टार',
    badge: 'वैदिक • KP • BNN • जैमिनी',
    title: 'कुंडलिस्टार के बारे में',
    hero:
      'कुंडलिस्टार एक आधुनिक ज्योतिष प्लेटफॉर्म है, जिसे वैदिक ज्योतिष, KP astrology, BNN, जैमिनी, अंक ज्योतिष, Astro-Vastu और advanced kundli analysis को आसान, व्यवस्थित और समझने योग्य बनाने के लिए बनाया गया है।',
    hero2:
      'हमारा उद्देश्य ज्योतिषियों, ज्योतिष विद्यार्थियों और spiritual seekers को जन्म कुंडली का विश्लेषण तेज़, गहराई से और अधिक आत्मविश्वास के साथ करने में मदद करना है — बिना अलग-अलग tools, notes, PDFs और manual calculations में उलझे।',
    createChart: 'कुंडली बनाएं',
    viewPlans: 'Plans देखें',
    focusTitle: 'हमारा फोकस',
    focus: [
      ['Accuracy', 'विश्वसनीय chart calculations और structured astrology logic.'],
      ['Depth', 'एक ही professional workspace में multiple Jyotish systems.'],
      ['Clarity', 'Readable charts, reports, tables और mobile-friendly views.'],
      ['Responsibility', 'Guidance awareness, learning और better decision-making के लिए.'],
    ],
    pillars: [
      {
        title: 'Advanced Astrology Tools',
        description:
          'कुंडलिस्टार Vedic, KP, BNN, Jaimini, Nadi, Panchang, transits, matching, numerology और report workflows को एक focused astrology platform में लाता है।',
        icon: <Calculate />,
      },
      {
        title: 'Real Practice के लिए बनाया गया',
        description:
          'यह platform astrologers, learners और serious seekers के लिए बनाया गया है जिन्हें repeatable calculations, saved charts, clean analysis views और professional reports की जरूरत होती है।',
        icon: <Science />,
      },
      {
        title: 'जिम्मेदारी के साथ Guidance',
        description:
          'Astrological interpretations को spiritual, educational और self-reflection guidance के रूप में प्रस्तुत किया जाता है। Important decisions के लिए users को personal judgment इस्तेमाल करने की सलाह दी जाती है।',
        icon: <Verified />,
      },
    ],
    whyTitle: 'हमने Kundlistar क्यों बनाया',
    why1:
      'Astrology work में अक्सर कई tools, saved charts, notes, PDFs, Panchang references और manual calculations के बीच switch करना पड़ता है। Kundlistar इन workflows को एक जगह लाता है ताकि users screens बदलने में कम और chart समझने में ज्यादा समय लगाएं।',
    why2:
      'हम Kundlistar को सिर्फ kundli generator नहीं, बल्कि एक complete astrology analysis ecosystem के रूप में बना रहे हैं। यह serious study, practical consultation और deeper interpretation के लिए बनाया गया है।',
    offersTitle: 'Kundlistar में क्या मिलेगा',
    capabilities: [
      'Vedic kundli generation',
      'KP astrology analysis',
      'Bhrigu Nandi Nadi workflows',
      'Jaimini astrology support',
      'Divisional charts',
      'Dasha और transit analysis',
      'Panchang और Navtara',
      'Horoscope matching',
      'Horary charts',
      'Personalised PDF reports',
      'Vedic numerology suggestions',
      'Astro-Vastu insights',
      'KP और BNN Vastu logic',
      'Panch Pakshi calculations',
      'Gemstone recommendations',
      'Web और mobile app access',
    ],
    missionTitle: 'हमारा Mission',
    mission:
      'हमारा mission advanced astrology को access, learn और apply करना आसान बनाना है। Kundlistar users को planetary combinations, dasha periods, transits, chart strengths, yogas, matching, numerology और Vastu insights को clean और organized platform में explore करने में मदद करता है।',
    visionTitle: 'हमारा Vision',
    vision:
      'हमारा vision Kundlistar को India से निकला हुआ एक trusted astrology SaaS platform बनाना है, जो Indian और global astrology users को accurate calculations, practical reports, mobile access और continuous feature improvements के माध्यम से serve करे।',
    audienceTitle: 'Astrologers, Learners और Seekers के लिए',
    audienceText:
      'Kundlistar उन सभी लोगों के लिए उपयोगी है जो surface-level astrology से आगे जाना चाहते हैं। चाहे आप Jyotish सीख रहे हों, consultations दे रहे हों, charts research कर रहे हों या अपनी life patterns समझना चाहते हों — platform आपको structured tools एक जगह देता है।',
    audiences: [
      'Professional astrologers',
      'Astrology students',
      'Spiritual seekers',
      'Jyotish researchers',
      'Detailed kundli reports चाहने वाले users',
      'Faster chart analysis चाहने वाले consultants',
    ],
    valuesTitle: 'हमारी Values',
    values: [
      {
        title: 'Accuracy',
        text: 'हम reliable chart calculations और structured astrological logic पर focus करते हैं ताकि users confidence के साथ काम कर सकें।',
      },
      {
        title: 'Depth',
        text: 'Kundlistar basic chart generation तक limited नहीं है, बल्कि multiple astrology systems को एक साथ जोड़ता है।',
      },
      {
        title: 'Clarity',
        text: 'Complex astrology concepts को clean charts, readable tables, guided reports और simple explanations के रूप में दिखाया जाता है।',
      },
      {
        title: 'Practical Use',
        text: 'हमारे tools career, marriage, finance, health, education, property और personal growth जैसे real-life areas के लिए बनाए गए हैं।',
      },
    ],
    ctaTitle: 'अपनी Astrology Journey शुरू करें',
    ctaText:
      'Birth chart बनाएं, Vedic/KP/BNN/Jaimini layers explore करें, analysis save करें और meaningful insights एक ही जगह प्राप्त करें।',
    ctaButton: 'कुंडली Calculate करें',
  },
};

const AboutUs: React.FC = () => {
  const [lang, setLang] = useState<Lang>('en');

  const t = useMemo(() => content[lang], [lang]);

  const handleLangChange = (_event: React.MouseEvent<HTMLElement>, value: Lang | null) => {
    if (value) setLang(value);
  };

  return (
    <Box sx={{ bgcolor: "var(--app-surface-neutral, #f7faf8)", minHeight: '100vh' }}>
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          pt: { xs: 6, md: 9 },
          pb: { xs: 7, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <ToggleButtonGroup
              value={lang}
              exclusive
              onChange={handleLangChange}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.14)',
                borderRadius: 2,
                '& .MuiToggleButton-root': {
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.25)',
                  px: 2,
                  fontWeight: 600,
                },
                '& .Mui-selected': {
                  bgcolor: "var(--app-surface-neutral, white) !important",
                  color: 'primary.main !important',
                },
              }}
            >
              <ToggleButton value="en">English</ToggleButton>
              <ToggleButton value="hi">हिंदी</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={t.brand}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.16)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={t.badge}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.16)',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              </Stack>

              <Typography
                variant="h2"
                component="h1"
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  mb: 2,
                  fontSize: { xs: '2.4rem', md: '4rem' },
                }}
              >
                {t.title}
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: 1.7,
                  maxWidth: 780,
                }}
              >
                {t.hero}
              </Typography>

              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.86)',
                  lineHeight: 1.8,
                  maxWidth: 760,
                  mt: 2,
                }}
              >
                {t.hero2}
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
                <Button
                  component={RouterLink}
                  to="/calculate"
                  variant="contained"
                  color="secondary"
                  size="large"
                  startIcon={<AutoAwesome />}
                >
                  {t.createChart}
                </Button>

                <Button
                  component={RouterLink}
                  to="/pricing"
                  variant="outlined"
                  size="large"
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.65)',
                  }}
                >
                  {t.viewPlans}
                </Button>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, md: 3 },
                  borderRadius: 2,
                  bgcolor: "var(--app-surface-neutral, rgba(255,255,255,0.95))",
                  border: '1px solid rgba(255,255,255,0.35)',
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                  {t.focusTitle}
                </Typography>

                <Stack spacing={1.25}>
                  {t.focus.map(([title, text]) => (
                    <Box key={title} sx={{ display: 'flex', gap: 1.5 }}>
                      <Box sx={{ color: "var(--app-ink-primary)", mt: 0.25 }}>
                        <Public fontSize="small" />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {text}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Grid container spacing={3} sx={{ mb: 5 }}>
          {t.pillars.map((pillar) => (
            <Grid key={pillar.title} size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  height: '100%',
                  p: 3,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ color: "var(--app-ink-primary)", mb: 1.5 }}>{pillar.icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {pillar.title}
                </Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {pillar.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            mb: 5,
          }}
        >
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
                {t.whyTitle}
              </Typography>

              <Typography color="text.secondary" sx={{ lineHeight: 1.8, mb: 2 }}>
                {t.why1}
              </Typography>

              <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                {t.why2}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 7 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {t.offersTitle}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {t.capabilities.map((item) => (
                  <Chip
                    key={item}
                    label={item}
                    variant="outlined"
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                p: { xs: 3, md: 4 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ color: "var(--app-ink-primary)", mb: 1.5 }}>
                <Psychology />
              </Box>

              <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
                {t.missionTitle}
              </Typography>

              <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                {t.mission}
              </Typography>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                p: { xs: 3, md: 4 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ color: "var(--app-ink-primary)", mb: 1.5 }}>
                <Insights />
              </Box>

              <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
                {t.visionTitle}
              </Typography>

              <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                {t.vision}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                p: { xs: 3, md: 4 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ color: "var(--app-ink-primary)", mb: 1.5 }}>
                <Groups />
              </Box>

              <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
                {t.audienceTitle}
              </Typography>

              <Typography color="text.secondary" sx={{ lineHeight: 1.8, mb: 2 }}>
                {t.audienceText}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {t.audiences.map((item) => (
                  <Chip
                    key={item}
                    label={item}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                ))}
              </Box>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                p: { xs: 3, md: 4 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: "var(--app-surface-neutral, #ffffff)",
              }}
            >
              <Box sx={{ color: "var(--app-ink-primary)", mb: 1.5 }}>
                <Description />
              </Box>

              <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
                {t.valuesTitle}
              </Typography>

              <Stack spacing={2}>
                {t.values.map((value) => (
                  <Box key={value.title}>
                    <Typography sx={{ fontWeight: 600 }}>{value.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {value.text}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 2,
            bgcolor: 'primary.main',
            color: 'white',
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                {t.ctaTitle}
              </Typography>

              <Typography sx={{ color: 'rgba(255,255,255,0.88)', lineHeight: 1.7 }}>
                {t.ctaText}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Button
                component={RouterLink}
                to="/calculate"
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<Favorite />}
              >
                {t.ctaButton}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default AboutUs;
